import React, { useEffect, useRef, useState } from 'react';
import { paymentService } from '../../services/paymentService';
import { toastHelper } from '../../utils/toastHelper';
import './PayPalButton.css';

// PayPal SDK types for orders (different namespace to avoid conflict with subscription SDK)
interface PayPalOrderSDK {
  Buttons: (config: {
    createOrder?: () => Promise<string>;
    onApprove?: (data: { orderID: string }) => Promise<void>;
    onCancel?: () => void;
    onError?: (err: any) => void;
    style?: {
      layout?: string;
      color?: string;
      shape?: string;
      label?: string;
      height?: number;
    };
  }) => {
    render: (selector: string) => Promise<void>;
    close: () => void;
  };
}

declare global {
  interface Window {
    paypalOrder?: PayPalOrderSDK;
  }
}

interface PayPalOrderButtonProps {
  containerId: string;
  paypalClientId: string;
  onSuccess?: () => void;
  disabled?: boolean;
}

/**
 * PayPal button for one-time order payments (lifetime access).
 * Uses PayPal Orders API instead of Subscriptions API.
 * Loads its own SDK instance with intent=capture to avoid conflicts with subscription SDK.
 */
const PayPalOrderButton: React.FC<PayPalOrderButtonProps> = ({
  containerId,
  paypalClientId,
  onSuccess,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef<boolean>(false);
  const buttonInstanceRef = useRef<{ close: () => void } | null>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  // Load PayPal SDK for orders (separate from subscription SDK)
  useEffect(() => {
    if (!paypalClientId) {
      console.error('PayPal client ID not provided');
      return;
    }

    // Check if order SDK is already loaded
    if (window.paypalOrder) {
      setSdkLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[data-paypal-order-sdk]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setSdkLoaded(true));
      return;
    }

    // Load PayPal SDK with intent=capture for one-time orders
    // Use data-namespace to avoid conflicts with subscription SDK
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&intent=capture&components=buttons&currency=USD`;
    script.async = true;
    script.crossOrigin = "anonymous"; // Required to get detailed error messages instead of "Script error."
    script.setAttribute('data-paypal-order-sdk', 'true');
    script.setAttribute('data-namespace', 'paypalOrder');
    script.onload = () => {
      setSdkLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load PayPal Order SDK');
    };
    document.body.appendChild(script);
  }, [paypalClientId]);

  // Render buttons when SDK is loaded
  useEffect(() => {
    if (!sdkLoaded || !window.paypalOrder || renderedRef.current) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Skip if container is not visible (in hidden tab)
    if (container.offsetParent === null) {
      return;
    }

    // Skip if disabled
    if (disabled) {
      return;
    }

    // Ensure container clips content during initial render (CSS handles this)
    // Remove loaded class to reset overflow to hidden
    container.classList.remove('paypal-loaded');

    try {
      const buttonInstance = window.paypalOrder!.Buttons({
        // Create order on our backend first
        createOrder: async () => {
          try {
            const orderData = await paymentService.createLifetimeOrder();
            return orderData.order_id;
          } catch (error: any) {
            console.error('Error creating lifetime order:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create order';
            toastHelper.newMessage('error', 'Error', errorMessage);
            throw error;
          }
        },

        // Capture order after PayPal approval
        onApprove: async (data: { orderID: string }) => {
          try {
            const captureResult = await paymentService.captureLifetimeOrder(data.orderID);

            if (captureResult.success) {
              toastHelper.newMessage(
                'success',
                'Welcome!',
                captureResult.message || `You are now Early Supporter #${captureResult.purchase_number}!`
              );

              if (onSuccess) {
                await onSuccess();
              }
            } else {
              toastHelper.newMessage(
                'info',
                'Purchase Incomplete',
                captureResult.message || 'The purchase was not completed. Please try again.'
              );
            }
          } catch (error: any) {
            console.error('Error capturing lifetime order:', error);

            // Check if it's because user already owns lifetime
            if (error?.response?.status === 400) {
              toastHelper.newMessage('info', 'Already Owned', 'You already have lifetime access!');
              if (onSuccess) {
                await onSuccess();
              }
            } else {
              const errorMessage = error?.response?.data?.message || error?.message || 'Failed to complete purchase';
              toastHelper.newMessage('error', 'Error', errorMessage);
            }
          }
        },

        onCancel: () => {
          toastHelper.newMessage('info', 'Cancelled', 'Purchase was cancelled.');
        },

        onError: (err: any) => {
          console.error('PayPal button error:', err);
          toastHelper.newMessage('error', 'Error', 'An error occurred with PayPal. Please try again.');
        },

        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'pay',
          height: 45,
        },
      });

      buttonInstanceRef.current = buttonInstance;

      // Render the buttons
      buttonInstance.render(`#${containerId}`)
        .then(() => {
          renderedRef.current = true;
        })
        .catch((err: Error) => {
          // PayPal SDK internal errors - these are often benign (e.g., container not visible)
          console.debug('[PayPal Order] Button render issue:', err?.message || err);
        });

      // Wait for PayPal to load and position correctly, then fade in
      const checkAndShow = () => {
        const iframe = container.querySelector('iframe');
        const paypalDiv = container.querySelector('[class*="paypal-buttons"]') as HTMLElement;

        if (iframe || paypalDiv) {
          // Fix PayPal height: 0px bug
          if (paypalDiv) {
            paypalDiv.style.setProperty('height', 'auto', 'important');
            paypalDiv.style.setProperty('min-height', '50px', 'important');
            paypalDiv.style.setProperty('position', 'relative', 'important');
            paypalDiv.style.setProperty('top', '0', 'important');
            paypalDiv.style.setProperty('left', '0', 'important');
            paypalDiv.style.setProperty('transform', 'none', 'important');
            paypalDiv.style.setProperty('margin', '0', 'important');
          }

          // Ensure iframe is positioned correctly
          if (iframe && iframe instanceof HTMLIFrameElement) {
            iframe.style.setProperty('position', 'relative', 'important');
            iframe.style.setProperty('top', '0', 'important');
            iframe.style.setProperty('left', '0', 'important');
            iframe.style.setProperty('transform', 'none', 'important');
            iframe.style.setProperty('margin', '0', 'important');
          }

          // Small delay to ensure PayPal has finished positioning, then allow overflow
          setTimeout(() => {
            container.classList.add('paypal-loaded');
          }, 100);
          return true;
        }
        return false;
      };

      // Check immediately, then every 100ms until loaded (max 2 seconds)
      let attempts = 0;
      const maxAttempts = 20;
      const checkInterval = setInterval(() => {
        attempts++;
        if (checkAndShow() || attempts >= maxAttempts) {
          clearInterval(checkInterval);
          if (attempts >= maxAttempts) {
            // Show anyway after timeout
            container.classList.add('paypal-loaded');
          }
        }
      }, 100);
    } catch (err) {
      console.error('Failed to render PayPal order button:', err);
      // Show container even on error
      if (container) {
        container.classList.add('paypal-loaded');
      }
    }

    return () => {
      // Clean up button instance if needed
      if (buttonInstanceRef.current) {
        try {
          buttonInstanceRef.current.close();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [sdkLoaded, containerId, onSuccess, disabled]);

  // Show loading state while SDK loads
  if (!sdkLoaded) {
    return (
      <div
        id={containerId}
        ref={containerRef}
        className="paypal-button-container w-full"
        style={{ colorScheme: 'none' }}
      >
        <div className="w-full h-12 bg-muted rounded-md animate-pulse flex items-center justify-center">
          <span className="text-sm text-muted-foreground">Loading PayPal...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      id={containerId}
      ref={containerRef}
      className="paypal-button-container w-full"
      style={{ colorScheme: 'none' }}
    />
  );
};

export default PayPalOrderButton;
